import type {
  AddCorrelation,
  AddCorrelations,
  AddCorrelationsBulkRequestData,
  CorrelationErrors,
  CorrelationsAddCorrelation
} from '@library/types/correlations';
import { findElementIndexInObjectList } from '@bo-utils/common';
import { pluck } from 'ramda';
import { selectUser } from '@bo-services/authenticationSlice';
import { useCallback } from 'react';
import { useCorrelationsPublishStatus } from '@bo-hooks/correlations/useCorrelationsPublishStatus';
import { useCreateCorrelationsMutation } from '@bo-services/correlationsApi';
import { useImmer } from 'use-immer';
import { useSelector } from 'react-redux';
import { v4 as uuid } from 'uuid';

export const useAddCorrelations = ({
  handleCorrelationsRefetch,
  handleResetCorrelations
}: {
  handleCorrelationsRefetch: () => Promise<void>;
  handleResetCorrelations: () => void;
}) => {
  const [newCorrelations, setNewCorrelations] = useImmer<AddCorrelations>([]);
  const {
    publishStatus: addCorrelationsStatus,
    resetPublishStatus: handleResetAddCorrelationsStatus,
    setPublishStatus: setaddCorrelationsStatus
  } = useCorrelationsPublishStatus();

  const [createCorrelations] = useCreateCorrelationsMutation();
  const user = useSelector(selectUser);

  const handleAddCorrelation = () => {
    setNewCorrelations((draft) => {
      const id = uuid();
      const newCorrelation: AddCorrelation = {
        approvedBy: undefined,
        correlationId: id,
        firstPosition: undefined,
        firstStatType: undefined,
        firstChoice: undefined,
        id,
        isSameTeam: false,
        secondChoice: undefined,
        secondPosition: undefined,
        secondStatType: undefined,
        probability: 0,
        requestedBy: undefined
      };

      draft.push(newCorrelation);
    });
  };

  const handleCorrelationsChange = useCallback(
    ({ initialCorrelation, key, value }: CorrelationsAddCorrelation) => {
      const { correlationId } = initialCorrelation;
      setNewCorrelations((draft) => {
        const draftCorrelationIndex = findElementIndexInObjectList(
          draft,
          'correlationId',
          correlationId
        );

        if (draftCorrelationIndex === -1) {
          return;
        }

        draft[draftCorrelationIndex][key] = value;
      });
      handleResetAddCorrelationsStatus();
    },
    [handleResetAddCorrelationsStatus, setNewCorrelations]
  );

  const handlePostPublishCorrelations = ({
    errorCorrelationIds
  }: {
    errorCorrelationIds: Array<string>;
  }) => {
    if (!errorCorrelationIds.length) {
      setNewCorrelations([]);
    }

    const errorCorrelations = newCorrelations.filter((newCorrelation) =>
      errorCorrelationIds.includes(newCorrelation.correlationId)
    );

    setNewCorrelations(errorCorrelations);
  };

  const handlePublishCorrelations = async ({
    correlations
  }: {
    correlations: AddCorrelations;
  }) => {
    const errors: CorrelationErrors = [];
    try {
      handleResetAddCorrelationsStatus();
      const bulkCorrelationRequestData: AddCorrelationsBulkRequestData =
        correlations.map(
          ({
            approvedBy,
            correlationId,
            firstPosition,
            firstStatType,
            firstChoice,
            isSameTeam,
            secondChoice,
            secondPosition,
            secondStatType,
            probability,
            requestedBy
          }) => ({
            approved_by: approvedBy || '',
            correlation_id: correlationId,
            first_choice: firstChoice || 'More',
            first_position: firstPosition || '',
            first_stat_type: firstStatType || '',
            is_same_team: isSameTeam,
            last_modified_user_name: user?.attributes.email || '',
            probability: Number(probability),
            second_choice: secondChoice || 'More',
            second_position: secondPosition || '',
            second_stat_type: secondStatType || '',
            requested_by: requestedBy || ''
          })
        );
      try {
        await createCorrelations(bulkCorrelationRequestData);
      } catch (error: unknown) {
        errors.push({ correlationId: 'bulk-request', error });
      }

      const hasErrors = errors.length > 0;
      const errorCorrelationIds = hasErrors
        ? pluck('correlationId', errors)
        : [];

      handlePostPublishCorrelations({ errorCorrelationIds });

      setaddCorrelationsStatus({
        isError: hasErrors,
        isLoading: false,
        isSuccess: !hasErrors,
        message: hasErrors ? 'Some or all requests have failed' : 'Success',
        errors
      });

      handleResetCorrelations();
      await handleCorrelationsRefetch();
    } catch (error) {
      setaddCorrelationsStatus({
        isError: true,
        isLoading: false,
        isSuccess: false,
        message: 'Unexpected error',
        errors: []
      });
    }
  };

  const handleDeleteCorrelation = useCallback(
    ({ correlationId }: { correlationId: string }) => {
      setNewCorrelations((draft) => {
        const draftCorrelationIndex = findElementIndexInObjectList(
          draft,
          'correlationId',
          correlationId
        );

        if (draftCorrelationIndex === -1) {
          return;
        }

        draft.splice(draftCorrelationIndex, 1);
      });
    },
    [setNewCorrelations]
  );

  return {
    handleAddCorrelation,
    handleCorrelationsChange,
    handleDeleteCorrelation,
    handlePublishCorrelations,
    handleResetAddCorrelationsStatus,
    addCorrelationsStatus,
    newCorrelations
  };
};
